import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useSelector } from 'react-redux';
import CustomDropdown from '../../custom/customDropdown';
import { AuthContext } from '../../Header/auth - context';
import './GPS.css';

/* GPS izveštaji po vozilu za zadati period.

   Podaci dolaze iz vehicle.GpsDnevniIzvestaj, koji jednom dnevno puni
   jobs/gpsIzvestaji.js sa Mobilisis-a. Izveštaj se zato otvara odmah i radi
   i kad je Mobilisis nedostupan — isti razlog zbog kog se čuvaju i pozicije.

   Svi tipovi izveštaja gledaju ISTI skup podataka; razlikuju se po tome koje
   kolone prikazuju i po čemu su poređani. Zato se ne šalje novi upit pri
   promeni tipa — menja se samo prikaz. */

// Sekunde -> "12h 30min". API sva trajanja daje u sekundama.
const sati = (s) => {
    if (s == null) return '';
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    return h ? `${h}h ${m}min` : `${m}min`;
};

const broj = (v, dec = 0) =>
    v == null ? '' : Number(v).toLocaleString('sr-RS', { minimumFractionDigits: dec, maximumFractionDigits: dec });

// Datum -> YYYY-MM-DD bez pomeranja u UTC (isti razlog kao toUtcIso u DnevniceHome).
const zaApi = (d) => {
    if (!d) return null;
    const x = new Date(d);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

/* Rad u leru je direktan trošak goriva — preko 30% udela se boji crveno, da se
   problematična vozila vide bez čitanja svakog reda. */
const bojaLera = (p) => (p >= 30 ? '#c62828' : p >= 15 ? '#ef6c00' : 'inherit');

const crveno = (v, dec) => (v > 0
    ? <span className="gps-izv-crveno">{broj(v, dec)}</span>
    : broj(v, dec));

/* Definicije izveštaja. Svaki nosi svoje kolone i podrazumevano sortiranje —
   izveštaj o gorivu se gleda po manjku, o leru po udelu lera, i tako dalje.
   Sve ide iz jednog upita; ovde se bira samo šta se pokazuje. */
const IZVESTAJI = (t) => ({
    ZBIRNI: {
        naziv: t('gps.izvZbirni'),
        sort: { field: 'km', order: -1 },
        kolone: [
            { f: 'dana', h: t('gps.dana'), w: 80 },
            { f: 'km', h: `${t('gps.predjeno')} (km)`, w: 110, b: r => broj(r.km, 1) },
            { f: 'vreme_voznje', h: t('gps.vremeVoznje'), w: 120, b: r => sati(r.vreme_voznje) },
            { f: 'ler', h: t('gps.ler'), w: 110, b: r => sati(r.ler) },
            { f: 'zaustavljanja', h: t('gps.zaustavljanja'), w: 110 },
            { f: 'brzina_maks', h: t('gps.maksBrzina'), w: 110, b: r => r.brzina_maks != null ? `${r.brzina_maks} km/h` : '' },
            { f: 'potrosnja_100km', h: t('gps.potrosnja'), w: 130, b: r => r.potrosnja_100km ? `${broj(r.potrosnja_100km, 2)} l/100km` : '' },
        ],
    },
    GORIVO: {
        naziv: t('gps.izvGorivo'),
        sort: { field: 'gorivo_manjak', order: -1 },
        kolone: [
            { f: 'km', h: `${t('gps.predjeno')} (km)`, w: 110, b: r => broj(r.km, 1) },
            { f: 'potrosnja_100km', h: `${t('gps.potrosnja')} (l/100km)`, w: 150, b: r => broj(r.potrosnja_100km, 2) },
            { f: 'gorivo_uneto', h: `${t('gps.gorivoTocenje')} (l)`, w: 130, b: r => broj(r.gorivo_uneto, 2) },
            { f: 'gorivo_izuzeto', h: `${t('gps.gorivoIstakanje')} (l)`, w: 140, b: r => crveno(r.gorivo_izuzeto, 2) },
            { f: 'gorivo_manjak', h: `${t('gps.gorivoManjak')} (l)`, w: 140, b: r => crveno(r.gorivo_manjak, 2) },
        ],
    },
    LER: {
        naziv: t('gps.izvLer'),
        sort: { field: 'ler', order: -1 },
        kolone: [
            { f: 'vreme_voznje', h: t('gps.vremeVoznje'), w: 130, b: r => sati(r.vreme_voznje) },
            { f: 'ler', h: t('gps.ler'), w: 130, b: r => sati(r.ler) },
            {
                f: 'ler_procenat', h: `${t('gps.lerUdeo')} %`, w: 120,
                b: r => <span style={{ color: bojaLera(r.ler_procenat), fontWeight: r.ler_procenat >= 15 ? 700 : 400 }}>
                    {broj(r.ler_procenat, 1)}%
                </span>
            },
            { f: 'zaustavljanja', h: t('gps.zaustavljanja'), w: 120 },
            { f: 'km', h: `${t('gps.predjeno')} (km)`, w: 110, b: r => broj(r.km, 1) },
        ],
    },
    BRZINA: {
        naziv: t('gps.izvBrzina'),
        sort: { field: 'km_prekoracenje', order: -1 },
        kolone: [
            { f: 'brzina_maks', h: t('gps.maksBrzina'), w: 130, b: r => r.brzina_maks != null ? `${r.brzina_maks} km/h` : '' },
            { f: 'km_prekoracenje', h: `${t('gps.prekoracenje')} (km)`, w: 150, b: r => crveno(r.km_prekoracenje, 1) },
            { f: 'vreme_prekoracenja', h: `${t('gps.prekoracenje')} — ${t('gps.vremeVoznje')}`, w: 170, b: r => sati(r.vreme_prekoracenja) },
            { f: 'km_van_radnog', h: `${t('gps.vanRadnog')} (km)`, w: 160, b: r => crveno(r.km_van_radnog, 1) },
            { f: 'km', h: `${t('gps.predjeno')} (km)`, w: 110, b: r => broj(r.km, 1) },
        ],
    },
    KILOMETRAZA: {
        naziv: t('gps.izvKilometraza'),
        sort: { field: 'km', order: -1 },
        kolone: [
            { f: 'dana', h: t('gps.dana'), w: 90 },
            { f: 'km', h: `${t('gps.predjeno')} (km)`, w: 120, b: r => broj(r.km, 1) },
            { f: 'km_po_danu', h: `${t('gps.prosekDnevno')} (km)`, w: 140, b: r => broj(r.dana ? r.km / r.dana : 0, 1) },
            { f: 'vreme_voznje', h: t('gps.vremeVoznje'), w: 130, b: r => sati(r.vreme_voznje) },
            { f: 'zaustavljanja', h: t('gps.zaustavljanja'), w: 120 },
        ],
    },
});

const Izvestaji = () => {
    const { t } = useTranslation();
    /* Dolazak sa GPS mape: pretraga salje vozilo i broj dana kroz stanje rute,
       pa se izvestaj otvara vec podesen umesto da korisnik ponovo bira ono sto
       je maloprepre izabrao. Cita se JEDNOM, pri prvom iscrtavanju — kasnije
       promene filtera su korisnikove i ne smeju da se vracaju unazad. */
    const izPretrage = useLocation().state || null;
    const auth = useContext(AuthContext);
    const company = useSelector(state => state.company);
    const toast = useRef(null);

    const danas = new Date();
    const preMesec = new Date();
    preMesec.setDate(preMesec.getDate() - (izPretrage?.dana > 0 ? izPretrage.dana : 30));

    const [tip, setTip] = useState('ZBIRNI');
    const [od, setOd] = useState(preMesec);
    const [doDatuma, setDoDatuma] = useState(danas);
    const [vozilo, setVozilo] = useState(izPretrage?.mobilisis_id || null);
    const [vozila, setVozila] = useState([]);
    const [redovi, setRedovi] = useState([]);
    const [ukupno, setUkupno] = useState(null);
    const [ucitava, setUcitava] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);

    const ziv = useRef(true);
    useEffect(() => () => { ziv.current = false; }, []);

    const definicije = useMemo(() => IZVESTAJI(t), [t]);
    const izvestaj = definicije[tip] || definicije.ZBIRNI;

    const tipOpcije = useMemo(
        () => Object.entries(definicije).map(([id, d]) => ({ id, name: d.naziv })),
        [definicije]
    );

    const generisi = useCallback(async () => {
        setUcitava(true);
        try {
            const r = await fetch(process.env.REACT_APP_BACKEND_URL + '/gps/izvestaj', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
                body: JSON.stringify({ od: zaApi(od), do: zaApi(doDatuma), mobilisis_id: vozilo }),
            });
            if (!r.ok) {
                const g = await r.json().catch(() => ({}));
                throw new Error(g.message || t('gps.greskaUcitavanja'));
            }
            const d = await r.json();
            if (!ziv.current) return;
            setRedovi(Array.isArray(d?.vozila) ? d.vozila : []);
            setUkupno(d?.ukupno || null);
        } catch (e) {
            if (ziv.current) {
                toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: e.message, life: 4000 });
            }
        } finally {
            if (ziv.current) setUcitava(false);
        }
    }, [auth.token, od, doDatuma, vozilo, t]);

    /* Spisak vozila za filter — isti izvor kao mapa. Greška se ne prijavljuje
       glasno: bez spiska se i dalje može tražiti izveštaj za celu flotu. */
    useEffect(() => {
        fetch(process.env.REACT_APP_BACKEND_URL + '/gps/zivo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({}),
        })
            .then(r => (r.ok ? r.json() : []))
            .then(d => { if (ziv.current) setVozila(Array.isArray(d) ? d : []); })
            .catch(() => {});
    }, [auth.token]);

    // Prvo punjenje; dalje ide na dugme, jer se izveštaj namerno ne osvežava uz svaki klik.
    useEffect(() => { generisi(); /* eslint-disable-next-line */ }, []);

    const opcije = useMemo(() => vozila.map(v => ({
        id: v.mobilisis_id,
        name: v.registracija,
    })), [vozila]);

    const opcijaIzabrana = useMemo(() => opcije.find(o => o.id === vozilo) || null, [opcije, vozilo]);
    const tipIzabran = useMemo(() => tipOpcije.find(o => o.id === tip) || null, [tipOpcije, tip]);

    /* Excel prati AKTIVNI izveštaj — izvozi se tačno ono što se vidi na ekranu. */
    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(izvestaj.naziv.slice(0, 30));

        const headers = [t('gps.registracija'), ...izvestaj.kolone.map(k => k.h)];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
            cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
        });

        (redovi || []).forEach(r => {
            /* U Excel ide BROJ, ne obojeni JSX iz tabele — zato se ne koristi
               `b` funkcija nego sirova vrednost, uz izuzetak izvedenih kolona. */
            const row = worksheet.addRow([
                r.registracija,
                ...izvestaj.kolone.map(k => (k.f === 'km_po_danu'
                    ? Number((r.dana ? r.km / r.dana : 0).toFixed(1))
                    : (r[k.f] ?? null))),
            ]);
            row.eachCell(cell => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });

        worksheet.columns.forEach(col => {
            let max = 10;
            col.eachCell(cell => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });

        const dt = new Date().toLocaleString('sr-RS', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade'
        }).replace(/,/g, '');

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(
            new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
            `${izvestaj.naziv} - ${company?.companyName || ''} - ${dt}.xlsx`
        );
    };

    const tableHeader = (
        <div className="export-buttons fakture-stavke-header">
            <div className="fakture-stavke-radnje">
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded
                    onClick={exportExcel} disabled={!redovi.length} data-pr-tooltip="XLS" />
                <strong className="gps-izv-naslov">{izvestaj.naziv}</strong>
            </div>
            <input type="search" placeholder={t('common.pretraga')} className="p-inputtext p-component"
                onInput={e => setGlobalFilter(e.target.value)} />
        </div>
    );

    return (
        <div className="gps-izvestaji">
            <Toast ref={toast} />
            <Tooltip target=".export-buttons>button" position="bottom" />

            <div className="gps-izv-filter">
                <span className="gps-izv-polje gps-izv-tip">
                    <label>{t('gps.tipIzvestaja')}</label>
                    <CustomDropdown
                        value={tipIzabran}
                        options={tipOpcije}
                        onChange={(e) => setTip(e.target.value ? e.target.value.id : 'ZBIRNI')}
                        placeholder={t('gps.tipIzvestaja')}
                        filter={false}
                        showClear={false}
                        style={{ width: '100%' }}
                    />
                </span>
                <span className="gps-izv-polje">
                    <label>{t('gps.odDatuma')}</label>
                    <Calendar value={od} onChange={(e) => setOd(e.value)} dateFormat="dd.mm.yy" showIcon />
                </span>
                <span className="gps-izv-polje">
                    <label>{t('gps.doDatuma')}</label>
                    <Calendar value={doDatuma} onChange={(e) => setDoDatuma(e.value)} dateFormat="dd.mm.yy" showIcon />
                </span>
                <span className="gps-izv-polje gps-izv-vozilo">
                    <label>{t('gps.vozilo')}</label>
                    <CustomDropdown
                        value={opcijaIzabrana}
                        options={opcije}
                        onChange={(e) => setVozilo(e.target.value ? e.target.value.id : null)}
                        placeholder={t('gps.svaVozila')}
                        filterPlaceholder={t('gps.pretraziRegistraciju')}
                        emptyMessage={t('gps.nemaVozila')}
                        style={{ width: '100%' }}
                    />
                </span>
                <Button type="button" icon="pi pi-cog" label={t('gps.generisi')}
                    loading={ucitava} onClick={generisi} />
            </div>

            {ukupno && (
                <div className="gps-izv-zbir">
                    <span><strong>{ukupno.od} — {ukupno.do}</strong></span>
                    <span>{t('gps.vozila')}: <strong>{broj(ukupno.vozila)}</strong></span>
                    <span>{t('gps.predjeno')}: <strong>{broj(ukupno.km, 1)} km</strong></span>
                    <span>{t('gps.ler')}: <strong>{sati(ukupno.ler)}</strong></span>
                    {ukupno.km_prekoracenje > 0 && (
                        <span className="gps-izv-crveno">
                            {t('gps.prekoracenje')}: <strong>{broj(ukupno.km_prekoracenje, 1)} km</strong>
                        </span>
                    )}
                    {ukupno.gorivo_manjak > 0 && (
                        <span className="gps-izv-crveno">
                            {t('gps.gorivoManjak')}: <strong>{broj(ukupno.gorivo_manjak, 2)} l</strong>
                        </span>
                    )}
                </div>
            )}

            <DataTable
                value={redovi || []}
                paginator rows={15}
                globalFilter={globalFilter}
                scrollable scrollHeight="52vh"
                header={tableHeader}
                resizableColumns columnResizeMode="expand"
                loading={ucitava}
                sortField={izvestaj.sort.field}
                sortOrder={izvestaj.sort.order}
                emptyMessage={t('gps.nemaPodatakaZaPeriod')}
                className="turePregledTable stavke-tabela">
                <Column field="registracija" header={t('gps.registracija')} sortable frozen style={{ minWidth: '130px' }} />
                {izvestaj.kolone.map(k => (
                    <Column
                        key={k.f}
                        field={k.f}
                        header={k.h}
                        sortable
                        style={{ minWidth: `${k.w}px` }}
                        body={k.b}
                    />
                ))}
            </DataTable>
        </div>
    );
};

export default Izvestaji;
